/**
 * WS client wrapper.
 *
 * Uses `reconnecting-websocket` so transient drops re-attempt with jitter
 * (Risk register: "WS reconnect storms", Med likelihood, mitigated here).
 *
 * Lifecycle (M2):
 *   1. open → send HELLO { clientVersion, protocolVersion }
 *   2. message → WELCOME (→ "connected")
 *   3. message → ROOM_STATE_SYNC (→ "in-room"; players + creatorId surfaced)
 *   4. reconnect → open fires again → HELLO carries saved sessionToken + roomId
 *      so server issues WELCOME + ROOM_STATE_SYNC without minting a new session.
 *
 * M2.T21: on reconnect the HELLO always carries the most-recently-seen
 * sessionToken and roomId, enabling the audience to re-sync from the server's
 * current canvas snapshot without losing their session.
 */

import ReconnectingWebSocket from "reconnecting-websocket";
import {
  PROTOCOL_VERSION,
  parseStrokeFrame,
  decodeColorIdx,
  FRAME_TYPE_STROKE_START,
  FRAME_TYPE_STROKE_BATCH,
  FRAME_TYPE_STROKE_END,
  type BrandColor,
  type CustomWord,
  type HelloFrame,
  type OpenStroke,
  type OutboundControlFrame,
  type RoomSettings,
  type SerializedPlayer,
  type TurnSummary,
  type WordChoice,
  type WordTier,
} from "@drawlol/shared";
import {
  appendStroke,
  appendFill,
  clearStrokes,
  hydrateStrokes,
  popStrokeById,
  beginProvisional,
  appendProvisionalSamples,
  endProvisional,
  clearProvisionals,
  type CanvasOp,
  type FinalizedStroke,
  type FinalizedFill,
} from "./canvas/strokesStore.js";

/**
 * M4 batch 6a: per-turn game state projected from the redacted snapshot +
 * incremental M4 frames.
 *
 * Field provenance:
 *  - `currentTurn.word` is the canonical word for drawer / audience-correct
 *    and `""` for audience-pending / spectator (per snapshotRedaction in
 *    the server). The client never reconstructs the word from
 *    revealedIndices: that's the server's call.
 *  - `currentTurn.wordLength` is always the canonical length so audience
 *    clients can render placeholders ("_ _ _ _ _") without ever seeing the
 *    word itself.
 *  - `wordChoices` is set only when the local player is the drawer at
 *    wordPick: the snapshot redactor strips it from everyone else.
 *  - `revealedIndices` is populated from the snapshot + appended via
 *    HINT_REVEALED frames (audience-pending only, drawer + spectator
 *    don't receive that frame per eventsToWire).
 *  - `correctGuesserIds` is populated from snapshot guesses + appended
 *    via GUESS_CORRECT frames (everyone receives those).
 */
export type GameStateView = {
  round: number;
  currentRound: number;
  currentTurnIndex: number;
  turnOrder: ReadonlyArray<string>;
  /**
   * v4 T7: drawing-phase total in seconds (room setting `drawTimerSeconds`),
   * projected from the snapshot's top-level `settings`. The in-game timer
   * divides `secondsLeft` by this to pick its urgency stop (calm/warning/
   * critical). Absent (e.g. a snapshot without settings) → the timer falls
   * back to the calm stop rather than a false alarm.
   */
  drawTimerSeconds?: number;
  /**
   * v4 T12: wordPick-phase total in seconds (room setting `wordPickTimerSeconds`),
   * projected from the snapshot's top-level `settings`. The audience's
   * WaitingForDrawer timer divides `secondsLeft` by THIS (not the drawing-phase
   * total) to pick its urgency stop, otherwise a short pick window read against
   * the longer draw total would sit on "critical" for the whole phase. Absent
   * (e.g. a snapshot without settings) → the timer falls back to the calm stop.
   */
  wordPickTimerSeconds?: number;
  /**
   * M4 batch 6c: completed-turn rollups. Drives the RevealPhaseShell
   * (last entry) + the GameOverShell scoreboard (full list). Always
   * an array; empty when the game just started.
   */
  turnSummaries: ReadonlyArray<TurnSummary>;
  currentTurn?: {
    drawerId: string;
    /** "" when redacted (audience-pending / spectator). */
    word: string;
    wordLength: number;
    /**
     * v4 T4, redaction-safe word skeleton ("___ _____"): letters/digits → "_",
     * spaces + punctuation verbatim. Set for audience-pending / spectator (the
     * `word === ""` recipients) so the placeholder shows multi-word breaks.
     * `undefined` for drawer / audience-correct (they render the real `word`).
     */
    wordPattern?: string;
    tier: WordTier;
    revealedIndices: ReadonlyArray<number>;
    /**
     * M4 batch 6c: index + character pairs at the revealed positions.
     * Carried out-of-band so audience-pending recipients (who see `word`
     * as `""`) can render the actual character at each revealed index.
     * Empty for spectators (no hints) and pre-hint audience-pending.
     */
    revealedLetters: ReadonlyArray<{ index: number; char: string }>;
    correctGuesserIds: ReadonlyArray<string>;
  };
  /** Present only when the local player is the drawer at wordPick phase. */
  wordChoices?: ReadonlyArray<WordChoice>;
  /**
   * Last server verdict for THIS player's guess (M4 batch 6b commit 3).
   * Populated by:
   *  - `GUESS_CORRECT` with `playerId === me` → `{kind:"correct", text, at}`
   *  - `GUESS_CLOSE` (whispered to guesser only) → `{kind:"close", text, at}`
   *  - `GUESS_MISS` (whispered to guesser only) → `{kind:"miss", text, at}`
   * The `<VerdictDisplay>` consumer clears itself after 3s; this field
   * stays set so a reconnect mid-drawing doesn't lose the last verdict
   * (though commit 3 doesn't yet snapshot verdicts, server reroutes
   * GUESS_CLOSE/MISS to the guesser only on the live wire).
   */
  lastVerdict?: { kind: "correct" | "close" | "miss"; text: string; at: number };
  /**
   * Drawer-disconnect grace state. Set by `DRAWER_DISCONNECT_GRACE` when the
   * active drawer's socket drops mid-{wordPick, drawing}; cleared by
   * `DRAWER_RECONNECT` (matching playerId) or by the next PHASE_CHANGED
   * snapshot push. The `<DrawerDisconnectBanner>` reads this to render a
   * "{name} disconnected: {countdown}s" overlay during the grace window.
   */
  drawerDisconnect?: { drawerId: string; graceUntil: number };
  /**
   * Last audience-leave notice received during an active game phase. Set by
   * `PLAYER_LEAVE_NOTICE`; carries the leaver's display name so the
   * `<LeaveNoticeToast>` can render "{name} left" without an additional
   * lookup. The toast self-clears via UI-side timer after a few seconds;
   * this field stays so a mid-fade re-render doesn't lose the data.
   */
  lastLeaveNotice?: { displayName: string; reason: "disconnect" | "leave"; at: number };
  /**
   * V4.2.T10b public wrong-guess feed (§5.12). A BOUNDED, oldest→newest list of
   * chat lines surfaced by `<GuessChatFeed>`. Appended on each CHAT_MESSAGE
   * frame + seeded from the redacted ROOM_STATE_SYNC backlog on (re)join.
   * Capped at `CHAT_LOG_CAP` with oldest-first eviction so the store can't grow
   * unbounded over a long turn. The server already redacts per recipient, the
   * drawer's copy never carries `miss` text, so this slice is render-safe.
   */
  chatLog: ReadonlyArray<ChatLine>;
};

/**
 * One rendered line in the public guess feed (V4.2.T10b). `key` is stable per
 * line so Preact can keyed-diff without re-animating settled rows; it is the
 * server backlog `id` when present, else a monotonic client counter.
 */
export type ChatLine = {
  key: string;
  playerId: string;
  name: string;
  /** Author's brand hex color: the feed tints the name in it (§C9). */
  color: string;
  kind: "miss" | "correct";
  /** Present for `miss`; absent for `correct`. */
  text?: string;
};

/**
 * Cap on the client-side chat feed (V4.2.T10b). Bounds store growth over a long
 * turn; the feed only shows ~8 rows so older lines aren't visible anyway. The
 * server caps its backlog independently (CHAT_LOG_CAP there); this is the
 * client's own eviction policy on the live + replayed stream.
 */
export const CHAT_LOG_CAP = 60;

export type ConnState =
  | { kind: "connecting" }
  | { kind: "connected"; playerId: string; sessionToken: string }
  | {
      kind: "in-room";
      playerId: string;
      sessionToken: string;
      roomId: string;
      players: ReadonlyArray<SerializedPlayer>;
      creatorId: string | null;
      /** M3: room kind for routing (freeDraw | game). */
      roomKind: string;
      /** M3: game phase for routing (lobby | wordPick | drawing | reveal | gameOver | freeDraw). */
      phase: string;
      /** M3: host player id (for host-only UI controls). */
      hostId: string;
      /** M3.T13: player IDs in the spectators set (joined mid-game). */
      spectatorIds: ReadonlyArray<string>;
      /** Wall-clock ms remaining in the current phase (from snapshot). */
      msRemaining: number;
      /**
       * Task #15: the room's canonical settings, projected from the
       * snapshot's top-level `settings` and kept fresh by SETTINGS_UPDATED
       * frames. Guests + migrated/resumed hosts render THESE instead of
       * the client-side defaults (which silently lied about the room).
       */
      settings?: RoomSettings;
      /** M4: per-game projection, undefined when phase=lobby. */
      game?: GameStateView;
      /**
       * Last in-room AUTH_REJECT (audit 2026-06-11). Set when the server
       * refuses an action on an OPEN socket (e.g. "not-authorized-start"
       * after a phase race): the lobby reads this to reset its
       * "Starting…" latch instead of soft-locking. Distinct from the
       * handshake-fatal reject, which becomes the terminal
       * `auth-rejected` state below.
       */
      lastAuthReject?: { reason: string; at: number };
    }
  | { kind: "reload-required"; serverProtocolVersion: number }
  | { kind: "room-gone"; roomId: string }
  /**
   * Terminal: the server closed this socket with code 1008 "kicked"
   * (host kick). Auto-reconnect is stopped, ReconnectingWebSocket would
   * otherwise silently rejoin with the still-alive session token.
   */
  | { kind: "kicked" }
  /**
   * Terminal: HELLO carried a session token the server no longer knows
   * ("invalid-session-token": server restart or evicted session).
   * Auto-reconnect is stopped; retrying the same dead token loops forever.
   * The shell falls back to a fresh join.
   */
  | { kind: "auth-rejected"; reason: string }
  | { kind: "error"; message: string };

export type WsClientOptions = {
  url: string;
  clientVersion: string;
  onState: (state: ConnState) => void;
  /**
   * If provided, HELLO will carry sessionToken + roomId for room join on the
   * FIRST connection. On subsequent reconnects these are superseded by the
   * session the server issued in the most recent WELCOME.
   */
  sessionToken?: string;
  roomId?: string;
  /**
   * M3.T6: Player display name (3-20 chars). Included in HELLO when joining
   * a game room so the server can register the player with their chosen name.
   */
  playerName?: string;
  /**
   * M3.T6: Player color (one of 8 brand hex colors). Included in HELLO when
   * joining a game room. Defaults to "#000000" if omitted server-side.
   */
  playerColor?: string;
};

/**
 * Outbound client → server JSON control frame (post-handshake).
 * Distinct from `WireFrame` (server → client). M3 shipped START_GAME +
 * UPDATE_SETTINGS + KICK_PLAYER; M4 batch 6a added PICK_WORD; batch 6b
 * adds SUBMIT_GUESS for the audience guess UI. SEND_REACTION / REMATCH
 * land in batch 6c when their UIs come online.
 */
export type ClientControlFrame =
  | { kind: "START_GAME" }
  | {
      kind: "UPDATE_SETTINGS";
      settings: {
        rounds?: number;
        drawTimerSeconds?: number;
        wordPickTimerSeconds?: number;
        hintsEnabled?: boolean;
        useCustomWords?: boolean;
        // T16 fold-in: the validated custom word list. The reducer's
        // RoomSettings already carries `customWords` and word-pick reads it
        // (wordPicker.ts): the client previously synced only the
        // useCustomWords boolean, so the words never reached the server. No
        // PROTOCOL_VERSION bump: the server forwards this straight into the
        // UPDATE_SETTINGS reducer action as Partial<RoomSettings>.
        customWords?: ReadonlyArray<CustomWord>;
      };
    }
  | { kind: "KICK_PLAYER"; playerId: string }
  | { kind: "PICK_WORD"; word: string; tier: WordTier }
  | { kind: "SUBMIT_GUESS"; text: string }
  | { kind: "REMATCH" }
  /**
   * M4 batch 7 commit 4: drawer-issued canvas mutations. The server's
   * inbound switch in `server/src/index.ts` dispatches the matching
   * reducer action; the reducer emits STROKE_UNDO_BROADCAST /
   * STROKE_CLEAR_BROADCAST, which the server fans out via eventsToWire.
   * Both kinds carry no payload: undo is "drop the last stroke from
   * `openStrokes`", clear is "wipe `openStrokes`".
   */
  | { kind: "STROKE_UNDO" }
  | { kind: "STROKE_CLEAR" }
  /**
   * v4 T1 (D5): drawer-issued flood fill. Unlike undo/clear it carries a
   * payload: the client mints `strokeId` (op id) and reuses it for the
   * optimistic local append so the server's FILL_BROADCAST echo dedups.
   * `point` is in CSS-pixel canvas space; `color` is a brand palette hex.
   */
  | { kind: "STROKE_FILL"; strokeId: string; point: { x: number; y: number }; color: BrandColor };

export type WsClient = {
  close: () => void;
  /**
   * Send a JSON control frame to the server.
   * Returns true if queued (socket open), false otherwise.
   * Safe to call when disconnected, the frame is dropped silently.
   */
  send: (frame: ClientControlFrame) => boolean;
  /**
   * Send a raw binary frame (M4 batch 7 stroke transport).
   * Used by the drawer's `<DrawingCanvas>` to push STROKE_START /
   * STROKE_BATCH / STROKE_END frames encoded via `wire-binary.ts`.
   * Returns true if queued (socket open), false otherwise.
   * Safe to call when disconnected, the frame is dropped silently.
   */
  sendBinary: (buf: ArrayBuffer) => boolean;
};

export function startWsClient(opts: WsClientOptions): WsClient {
  const { url, clientVersion, onState } = opts;
  onState({ kind: "connecting" });

  const rws = new ReconnectingWebSocket(url, [], {
    maxReconnectionDelay: 5000,
    minReconnectionDelay: 500,
    reconnectionDelayGrowFactor: 1.3,
    connectionTimeout: 4000,
    maxRetries: Infinity,
  });

  /**
   * Session token issued by the most recent WELCOME. Sent in every subsequent
   * HELLO so the server can reconnect the client to their existing session.
   * Supersedes opts.sessionToken after the first WELCOME.
   */
  let resumeSessionToken: string | null = opts.sessionToken ?? null;

  /**
   * Room ID to include in HELLO on reconnect. Set when ROOM_STATE_SYNC is
   * received (i.e. after a successful room join). Supersedes opts.roomId.
   */
  let resumeRoomId: string | null = opts.roomId ?? null;

  /** Player ID from the most recent WELCOME (needed for in-room state). */
  let currentPlayerId: string | null = null;

  rws.addEventListener("open", () => {
    const hello: HelloFrame = {
      kind: "HELLO",
      clientVersion,
      protocolVersion: PROTOCOL_VERSION,
      ...(resumeSessionToken ? { sessionToken: resumeSessionToken } : {}),
      ...(resumeRoomId ? { roomId: resumeRoomId } : {}),
      ...(opts.playerName ? { name: opts.playerName } : {}),
      ...(opts.playerColor ? { color: opts.playerColor } : {}),
    };
    rws.send(JSON.stringify(hello));
  });

  /**
   * Last in-room state we surfaced to the consumer. Cached here so the M3
   * partial-update frames (PLAYER_JOIN_BROADCAST, PHASE_CHANGED, HOST_CHANGED,
   * SETTINGS_UPDATED, PLAYER_KICKED, SPECTATORS_PROMOTED) can patch the
   * relevant slice without forcing a full ROOM_STATE_SYNC round-trip. M4
   * adds WORD_PICKED, WORD_HIDDEN, HINT_REVEALED, GUESS_CORRECT to the
   * frames that mutate `game` without a fresh snapshot.
   */
  let lastInRoom: Extract<ConnState, { kind: "in-room" }> | null = null;

  function patchInRoom(patch: Partial<Extract<ConnState, { kind: "in-room" }>>): void {
    if (!lastInRoom) return;
    lastInRoom = { ...lastInRoom, ...patch };
    onState(lastInRoom);
  }

  /**
   * Apply an updater to the cached `lastInRoom.game` slice and surface a
   * fresh ConnState. No-op when there's no in-room state or no game (lobby
   * phase). The updater receives the prior game (or `undefined` if absent)
   * and must return the new game (or `undefined` to clear it). Always
   * returns a fresh game object so consumers can rely on identity-equality
   * for change detection.
   */
  function patchGame(
    updater: (prior: GameStateView | undefined) => GameStateView | undefined,
  ): void {
    if (!lastInRoom) return;
    const nextGame = updater(lastInRoom.game);
    // exactOptionalPropertyTypes: only include `game` when defined; otherwise
    // patch a "without-game" snapshot by rebuilding the in-room state.
    if (nextGame === undefined) {
      const { game: _drop, ...rest } = lastInRoom;
      lastInRoom = { ...rest };
      onState(lastInRoom);
    } else {
      patchInRoom({ game: nextGame });
    }
  }

  /** Monotonic client-side fallback key for chat lines with no server `id`. */
  let chatKeySeq = 0;

  /**
   * Append a chat line to the cached game's bounded `chatLog`, evicting
   * oldest-first past CHAT_LOG_CAP. No-op when there's no game slice (lobby).
   * Always returns a fresh game object so consumers can identity-diff.
   */
  function appendChatLine(line: ChatLine): void {
    patchGame((prior) => {
      if (!prior) return prior;
      const next = [...prior.chatLog, line];
      const bounded = next.length > CHAT_LOG_CAP ? next.slice(next.length - CHAT_LOG_CAP) : next;
      return { ...prior, chatLog: bounded };
    });
  }

  /**
   * Project the redacted ROOM_STATE_SYNC `chatLog` backlog (top-level frame
   * field) into client `ChatLine`s. Defensive: tolerates a missing/garbled
   * field by returning an empty list. Bounded to CHAT_LOG_CAP (oldest-first).
   */
  function projectChatBacklog(raw: unknown): ChatLine[] {
    if (!Array.isArray(raw)) return [];
    const lines: ChatLine[] = [];
    for (const e of raw) {
      if (!e || typeof e !== "object") continue;
      const m = e as Record<string, unknown>;
      if (m["chatKind"] !== "miss" && m["chatKind"] !== "correct") continue;
      lines.push({
        key: `s${typeof m["id"] === "number" ? m["id"] : `b${chatKeySeq++}`}`,
        playerId: typeof m["playerId"] === "string" ? (m["playerId"] as string) : "",
        name: typeof m["name"] === "string" ? (m["name"] as string) : "",
        color: typeof m["color"] === "string" ? (m["color"] as string) : "var(--ink)",
        kind: m["chatKind"] as "miss" | "correct",
        ...(typeof m["text"] === "string" ? { text: m["text"] as string } : {}),
      });
    }
    return lines.length > CHAT_LOG_CAP ? lines.slice(lines.length - CHAT_LOG_CAP) : lines;
  }

  /**
   * Project the server-side redacted RoomSnapshot.state.game into a
   * GameStateView for the client store. Returns undefined when there's
   * no game (lobby phase) or when the inbound shape is malformed (defensive
   *: the server is supposed to send a clean shape but we don't want a
   * stray field to crash the message dispatch).
   *
   * Maps from the wire snapshot's Set / Map / array fields to JSON-safe
   * arrays in the projected view.
   */
  function projectGameFromSnapshot(state: unknown): GameStateView | undefined {
    if (!state || typeof state !== "object") return undefined;
    const s = state as Record<string, unknown>;
    const rawGame = s["game"];
    if (!rawGame || typeof rawGame !== "object") return undefined;
    const g = rawGame as Record<string, unknown>;
    const turnOrder = Array.isArray(g["turnOrder"]) ? (g["turnOrder"] as string[]) : [];
    const currentTurnIndex = typeof g["currentTurnIndex"] === "number" ? g["currentTurnIndex"] : 0;
    const currentRound = typeof g["currentRound"] === "number" ? g["currentRound"] : 0;
    const rounds = typeof g["rounds"] === "number" ? g["rounds"] : 0;

    // M4 batch 6c: project turnSummaries verbatim, already JSON-safe
    // (no Maps/Sets inside; the new `guesses` array uses plain objects).
    const rawSummaries = g["turnSummaries"];
    const turnSummaries: ReadonlyArray<TurnSummary> = Array.isArray(rawSummaries)
      ? (rawSummaries as TurnSummary[])
      : [];

    // v4 T7/T12: the timer totals live at the snapshot's top-level
    // `settings` (serializeRoomState carries it verbatim), not inside `game`.
    const rawSettings = s["settings"];
    const settingsObj =
      rawSettings && typeof rawSettings === "object"
        ? (rawSettings as Record<string, unknown>)
        : undefined;
    const drawTimerSeconds = settingsObj?.["drawTimerSeconds"];
    const wordPickTimerSeconds = settingsObj?.["wordPickTimerSeconds"];

    const view: GameStateView = {
      round: rounds,
      currentRound,
      currentTurnIndex,
      turnOrder,
      turnSummaries,
      // Seeded from the frame-level `chatLog` backlog by the ROOM_STATE_SYNC
      // handler (the backlog lives on the frame, not inside `state.game`).
      chatLog: [],
      ...(typeof drawTimerSeconds === "number" ? { drawTimerSeconds } : {}),
      ...(typeof wordPickTimerSeconds === "number" ? { wordPickTimerSeconds } : {}),
    };

    const rawTurn = g["currentTurn"];
    if (rawTurn && typeof rawTurn === "object") {
      const t = rawTurn as Record<string, unknown>;
      // M4 batch 6b: the server now runs the snapshot through
      // `serializeRoomState` before sending, so revealedIndices arrives as
      // a `number[]` and reactionsByPlayer as `[PlayerId, number][]`. The
      // legacy Set/Map fallback below is defensive, it stays so a future
      // bypass of the serializer doesn't silently drop hints, but the
      // happy path is the Array branch.
      let revealed: number[] = [];
      const ri = t["revealedIndices"];
      if (Array.isArray(ri)) revealed = ri as number[];
      else if (ri instanceof Set) revealed = Array.from(ri as Set<number>);
      // M4 batch 6c: parallel `revealedLetters` projection on the wire.
      // Defensive when the server forgets to include it (older snapshot
      // format): fall back to an empty array.
      let revealedLetters: ReadonlyArray<{ index: number; char: string }> = [];
      const rl = t["revealedLetters"];
      if (Array.isArray(rl)) {
        revealedLetters = rl.filter(
          (e): e is { index: number; char: string } =>
            !!e &&
            typeof e === "object" &&
            typeof (e as Record<string, unknown>)["index"] === "number" &&
            typeof (e as Record<string, unknown>)["char"] === "string",
        );
      }
      const correctGuesserIds: string[] = [];
      const guesses = t["guesses"];
      if (Array.isArray(guesses)) {
        for (const ge of guesses) {
          if (ge && typeof ge === "object") {
            const pid = (ge as Record<string, unknown>)["playerId"];
            if (typeof pid === "string") correctGuesserIds.push(pid);
          }
        }
      }
      view.currentTurn = {
        drawerId: typeof t["drawerId"] === "string" ? (t["drawerId"] as string) : "",
        word: typeof t["word"] === "string" ? (t["word"] as string) : "",
        wordLength: typeof t["wordLength"] === "number" ? (t["wordLength"] as number) : 0,
        // v4 T4: carried only on the redacted snapshot (audience-pending /
        // spectator). Absent → undefined → WordSkeleton falls back to the
        // length-only underscore row.
        ...(typeof t["wordPattern"] === "string"
          ? { wordPattern: t["wordPattern"] as string }
          : {}),
        tier: (t["tier"] as WordTier | undefined) ?? "medium",
        revealedIndices: revealed,
        revealedLetters,
        correctGuesserIds,
      };
    }

    const choices = g["wordChoices"];
    if (Array.isArray(choices)) view.wordChoices = choices as ReadonlyArray<WordChoice>;

    return view;
  }

  /**
   * M4 batch 7 commit 3: per-strokeIdNonce metadata recovered from the
   * STROKE_START broadcasts. STROKE_END_BROADCAST frames carry only the
   * canonical SVG path + the strokeIdNonce, color, brushSize, isEraser
   * came across in STROKE_START. We pair them up here so the client can
   * push a complete FinalizedStroke into the local store.
   *
   * Best-effort: if the STROKE_END arrives without a matching START
   * (e.g. nonce=0 fallback, race on join, or future server-side
   * coalescing) we fall back to a default-black brush and log a warn.
   * The audience still sees the stroke; just with stand-in metadata.
   */
  type PendingStrokeMeta = {
    color: FinalizedStroke["color"];
    brushSize: FinalizedStroke["brushSize"];
    isEraser: FinalizedStroke["isEraser"];
  };
  const pendingStrokeMeta = new Map<number, PendingStrokeMeta>();

  /**
   * v4 T3: count of THIS connection's own STROKE_CLEAR frames still awaiting
   * their STROKE_CLEAR_BROADCAST echo. The drawer clears locally (flushing any
   * held stroke) and sends STROKE_CLEAR; the server echoes the broadcast back
   * to every socket: including this one. That echo is redundant for the
   * originating session (already flushed) and must NOT re-reset the canvas, or
   * it would drop a NEW stroke begun during the round-trip. But a broadcast
   * with no pending echo is an EXTERNAL clear (e.g. the same drawer's other
   * tab/device) and MUST flush this session's in-progress stroke. Paired here
   * because this module owns both the send and the receive.
   */
  let pendingClearEchoes = 0;

  function handleBinaryFrame(buf: ArrayBuffer): void {
    let parsed;
    try {
      parsed = parseStrokeFrame(buf);
    } catch (err) {
      // Malformed binary frame from the server, log and drop.
      console.warn("ws: malformed binary frame; dropped", err);
      return;
    }
    if (parsed.frameType === FRAME_TYPE_STROKE_START) {
      const { color, isEraser } = decodeColorIdx(parsed.colorIdx);
      pendingStrokeMeta.set(parsed.strokeIdNonce, {
        color,
        brushSize: parsed.brushSize,
        isEraser,
      });
      // Live mid-stroke render (audit 2026-06-11): open a provisional entry,
      // STROKE_BATCH samples accumulate into it and readOnly canvases paint
      // the polyline as it grows. The drawer's own echo lands here too but is
      // never drawn (its canvas isn't readOnly); STROKE_END swaps everyone to
      // the canonical smoothed path.
      beginProvisional({
        strokeIdNonce: parsed.strokeIdNonce,
        color,
        brushSize: (parsed.brushSize as 0 | 1 | 2) ?? 1,
        isEraser,
      });
      return;
    }
    if (parsed.frameType === FRAME_TYPE_STROKE_END) {
      const meta = pendingStrokeMeta.get(parsed.strokeIdNonce);
      pendingStrokeMeta.delete(parsed.strokeIdNonce);
      const color = meta?.color ?? "#000000";
      const brushSize = meta?.brushSize ?? 1;
      const isEraser = meta?.isEraser ?? false;
      if (!meta) {
        console.warn(
          `ws: STROKE_END for nonce ${parsed.strokeIdNonce} with no prior STROKE_START — using defaults`,
        );
      }
      appendStroke({
        strokeId: String(parsed.strokeIdNonce),
        path: parsed.path,
        color,
        brushSize,
        isEraser,
      });
      // Commit first, then drop the provisional, same tick, so readOnly
      // canvases never show a gap between the polyline and the final path.
      endProvisional(parsed.strokeIdNonce);
      return;
    }
    if (parsed.frameType === FRAME_TYPE_STROKE_BATCH) {
      // Live mid-stroke render (audit 2026-06-11): the server already
      // rebroadcasts these in real time; they used to be dropped here.
      appendProvisionalSamples(parsed.strokeIdNonce, parsed.samples);
      return;
    }
  }

  /**
   * M4 batch 7 commit 3: project the snapshot's `openStrokes` array into
   * the local `$finalizedStrokes` store. Reconnect or late-join clients
   * use this to catch up to the current canvas without waiting for new
   * STROKE_END_BROADCAST frames.
   *
   * `OpenStroke` is now a stroke|fill union (v4 T1). Fill ops project straight
   * across (point + color, no path). Stroke ops carry `path?`/`isEraser?`
   * optionally; we skip any stroke without a `path` (a stroke captured
   * pre-STROKE_END, which the server doesn't serialize today but the type
   * allows). Each entry's `color` (and a stroke's `brushSize`) are verbatim.
   */
  function hydrateStrokesFromSnapshot(state: unknown): void {
    // A snapshot replaces the canvas truth, any in-flight provisional is
    // stale (its STROKE_END either landed in the snapshot or never will).
    clearProvisionals();
    if (!state || typeof state !== "object") {
      hydrateStrokes([]);
      return;
    }
    const s = state as Record<string, unknown>;
    const raw = s["openStrokes"];
    if (!Array.isArray(raw)) {
      hydrateStrokes([]);
      return;
    }
    const ops: CanvasOp[] = [];
    for (const entry of raw as ReadonlyArray<OpenStroke>) {
      if (!entry || typeof entry !== "object") continue;
      // v4 T1: fill ops carry no path, project them straight across so a
      // reconnecting / late-joining client replays them in order.
      if (entry.kind === "fill") {
        ops.push({
          kind: "fill",
          strokeId: entry.strokeId,
          point: entry.point,
          color: entry.color as FinalizedFill["color"],
        });
        continue;
      }
      // Stroke op: skip any entry without a finalized path (a stroke captured
      // pre-STROKE_END: the server doesn't serialize those today).
      if (typeof entry.path !== "string" || entry.path.length === 0) continue;
      // BrushSize on the wire is `number`; coerce to the 0|1|2 union
      // (the renderer is tolerant: the type narrows to satisfy TS).
      const brushSize = (
        entry.brushSize === 0 || entry.brushSize === 1 || entry.brushSize === 2
          ? entry.brushSize
          : 1
      ) as FinalizedStroke["brushSize"];
      ops.push({
        kind: "stroke",
        strokeId: entry.strokeId,
        path: entry.path,
        color: entry.color as FinalizedStroke["color"],
        brushSize,
        isEraser: entry.isEraser === true,
      });
    }
    hydrateStrokes(ops);
  }

  rws.addEventListener("message", (ev: MessageEvent) => {
    // M4 batch 7 commit 3: binary frames are STROKE_* transport. Pre-batch-7
    // the client dropped them on the floor; now we route them through
    // `handleBinaryFrame` to populate the local `$finalizedStrokes` store.
    if (ev.data instanceof ArrayBuffer) {
      handleBinaryFrame(ev.data);
      return;
    }
    if (ev.data instanceof Blob) {
      // Most browsers default ws.binaryType to "blob"; we keep the default
      // but convert here. (ReconnectingWebSocket honours `binaryType`; if a
      // future setting flips it to "arraybuffer" the above branch handles it.)
      ev.data
        .arrayBuffer()
        .then(handleBinaryFrame)
        .catch(() => {});
      return;
    }

    let frame: OutboundControlFrame | undefined;
    try {
      frame = JSON.parse(String(ev.data)) as OutboundControlFrame;
    } catch {
      onState({ kind: "error", message: "bad frame from server" });
      return;
    }
    switch (frame.kind) {
      case "WELCOME":
        currentPlayerId = frame.playerId;
        // Persist for reconnect: the server issued this session token and
        // will accept it in future HELLO frames to resume the same session.
        resumeSessionToken = frame.sessionToken;
        onState({
          kind: "connected",
          playerId: frame.playerId,
          sessionToken: frame.sessionToken,
        });
        break;
      case "ROOM_STATE_SYNC":
        // Persist roomId so reconnect HELLO can re-join the same room.
        if (resumeRoomId === null && opts.roomId) resumeRoomId = opts.roomId;
        // M4 batch 7 commit 3: hydrate `$finalizedStrokes` from the
        // snapshot's `openStrokes` array. This is what makes reconnect +
        // late-join clients catch up to the current canvas state without
        // waiting for new STROKE_END_BROADCAST frames. We also clear the
        // pending-meta map (any half-collected START → END pairs from
        // before the sync are now stale).
        hydrateStrokesFromSnapshot(frame.snapshot?.state);
        pendingStrokeMeta.clear();
        // v4 T3: a ROOM_STATE_SYNC is the authoritative canvas reset, so any
        // un-echoed STROKE_CLEAR accounting is now stale. Reset the counter so a
        // clear whose echo was lost (disconnect before delivery, a reconnect
        // always lands here) can't make a later EXTERNAL clear look like our own
        // echo and skip the in-progress reset.
        pendingClearEchoes = 0;
        if (currentPlayerId && resumeSessionToken) {
          const snapState = frame.snapshot?.state as
            | { kind?: string; phase?: string; hostId?: string }
            | undefined;
          const projectedGame = projectGameFromSnapshot(frame.snapshot?.state);
          // V4.2.T10b: seed the public chat feed from the redacted late-joiner
          // backlog (frame-level `chatLog`). Replaces (not appends) so a
          // reconnect doesn't double-count lines it already had.
          if (projectedGame) {
            projectedGame.chatLog = projectChatBacklog((frame as { chatLog?: unknown }).chatLog);
          }
          // Task #15: the snapshot carries the room's canonical settings at
          // the top level (serializeRoomState passes them verbatim), for
          // EVERY phase including lobby, where projectGameFromSnapshot
          // returns undefined. Defensive shape check only; the values are
          // server-validated.
          const rawSettings = (frame.snapshot?.state as Record<string, unknown> | undefined)?.[
            "settings"
          ];
          const projectedSettings =
            rawSettings && typeof rawSettings === "object" && !Array.isArray(rawSettings)
              ? (rawSettings as RoomSettings)
              : undefined;
          const inRoom: Extract<ConnState, { kind: "in-room" }> = {
            kind: "in-room",
            playerId: currentPlayerId,
            sessionToken: resumeSessionToken,
            roomId: resumeRoomId ?? "",
            players: frame.players,
            creatorId: frame.creatorId,
            // M3: surface room kind + phase for routing in RoomShell
            roomKind: snapState?.kind ?? "freeDraw",
            phase: snapState?.phase ?? "lobby",
            hostId: snapState?.hostId ?? "",
            // M3.T13: spectator set (JSON-safe array from server)
            spectatorIds: Array.isArray(frame.spectatorIds) ? frame.spectatorIds : [],
            msRemaining: frame.snapshot?.msRemaining ?? 0,
            // Task #15: canonical room settings (lobby display truth).
            ...(projectedSettings !== undefined ? { settings: projectedSettings } : {}),
            // M4: project the redacted game state. Omit the field entirely
            // for lobby / freeDraw rooms (exactOptionalPropertyTypes).
            ...(projectedGame !== undefined ? { game: projectedGame } : {}),
          };
          lastInRoom = inRoom;
          onState(inRoom);
        }
        break;
      case "PLEASE_RELOAD":
        onState({
          kind: "reload-required",
          serverProtocolVersion: frame.serverProtocolVersion,
        });
        rws.close(1000, "protocol-mismatch");
        break;
      case "ROOM_GONE":
        onState({ kind: "room-gone", roomId: frame.roomId });
        rws.close(1000, "room-gone");
        break;
      // M3.fix B6: partial-update frames
      case "PLAYER_JOIN_BROADCAST": {
        if (!lastInRoom) break;
        const existing = lastInRoom.players;
        if (existing.some((p) => p.id === frame.player.id)) break; // dedupe
        patchInRoom({ players: [...existing, frame.player] });
        break;
      }
      case "PHASE_CHANGED":
        // V4.2.T12 (note 5e): stale-flash fix. The client `$finalizedStrokes`
        // store only cleared on STROKE_CLEAR_BROADCAST / hydrate. On wordPick
        // ENTRY (before any fresh ROOM_STATE_SYNC arrives) the prior turn's
        // drawing would otherwise FLASH on the audience read-only canvas. A
        // wordPick transition is a canvas reset → wipe the local stroke log.
        // resetInProgress=false: audience clients are readOnly (no in-progress
        // stroke to flush), and the drawer is routed to the pick UI (no canvas).
        //
        // V4.2.T13 (note 5h.1): clear ONLY on → wordPick, NOT on → reveal. The
        // reducer now keeps `openStrokes` through reveal (cleared on reveal
        // EXIT), so the kept drawing must persist in the local store too, the
        // reveal screen renders it under the summary card. Clearing here on
        // wordPick is exactly the reveal-EXIT mirror that prevents the prior
        // drawing leaking onto the next turn.
        if (frame.nextPhase === "wordPick") {
          clearStrokes(false);
          clearProvisionals();
        }
        patchInRoom({ phase: frame.nextPhase });
        break;
      case "HOST_CHANGED":
        patchInRoom({ hostId: frame.newHostId });
        break;
      case "SETTINGS_UPDATED":
        // Task #15: the frame carries the FULL canonical RoomSettings,
        // hold it on ConnState so guests (and migrated or resumed hosts)
        // render the room's real settings instead of the client-side
        // DEFAULT_SETTINGS lie. (Was a no-op patch for years.)
        if (lastInRoom) patchInRoom({ settings: frame.settings });
        break;
      case "PLAYER_KICKED":
      case "PLAYER_LEFT_BROADCAST":
        // Roster prune. PLAYER_LEFT_BROADCAST is protocol 8 (audit Q9),
        // roster truth on leave; before it, leavers ghosted in every roster
        // until the next snapshot. Both frames prune the spectator set too
        // (review F4): the reducer deletes kicked/leaving ids from BOTH
        // sets, and a raw KICK_PLAYER frame can target a spectator even
        // though the host UI only renders kick buttons for players.
        if (lastInRoom) {
          patchInRoom({
            players: lastInRoom.players.filter((p) => p.id !== frame.playerId),
            spectatorIds: lastInRoom.spectatorIds.filter((id) => id !== frame.playerId),
          });
        }
        break;
      case "SPECTATORS_PROMOTED":
        if (lastInRoom) {
          // After promotion the spectator set is empty.
          patchInRoom({ spectatorIds: [] });
        }
        break;

      // ── M4 batch 6a: game-loop frames ───────────────────────────────────
      // The server routes these per role via eventsToWire: WORD_PICKED lands
      // only on drawer + audience-correct sockets; WORD_HIDDEN lands on
      // audience-pending + spectator; HINT_REVEALED lands on audience-pending
      // only; GUESS_CORRECT broadcasts to all. So we don't need to second-
      // guess routing here: whatever shows up updates our local view.

      case "WORD_PICKED":
        patchGame((prior) => {
          if (!prior?.currentTurn) return prior;
          return {
            ...prior,
            currentTurn: {
              ...prior.currentTurn,
              word: frame.word,
              wordLength: frame.word.length,
              tier: frame.tier,
            },
          };
        });
        break;

      case "WORD_HIDDEN":
        patchGame((prior) => {
          if (!prior?.currentTurn) return prior;
          return {
            ...prior,
            currentTurn: {
              ...prior.currentTurn,
              word: "",
              wordLength: frame.wordLength,
              // v4 T4: structural skeleton for the multi-word placeholder.
              wordPattern: frame.wordPattern,
              tier: frame.tier,
            },
          };
        });
        break;

      case "HINT_REVEALED":
        patchGame((prior) => {
          if (!prior?.currentTurn) return prior;
          // M4 batch 6c: parallel letter projection so the WordSkeleton
          // can render actual letters when `word` is blanked. Defensive
          // copy + Array.isArray gate so older server builds (or hand-
          // crafted test frames) that send only `revealedIndices` don't
          // throw: they just skip the letter update.
          const newLetters = Array.isArray(frame.revealedLetters)
            ? [...frame.revealedLetters]
            : prior.currentTurn.revealedLetters;
          return {
            ...prior,
            currentTurn: {
              ...prior.currentTurn,
              // Server is authoritative: replace rather than append.
              revealedIndices: [...frame.revealedIndices],
              revealedLetters: newLetters,
            },
          };
        });
        break;

      case "GUESS_CORRECT":
        patchGame((prior) => {
          if (!prior?.currentTurn) return prior;
          const existing = prior.currentTurn.correctGuesserIds;
          const next: GameStateView = { ...prior };
          if (!existing.includes(frame.playerId)) {
            next.currentTurn = {
              ...prior.currentTurn,
              correctGuesserIds: [...existing, frame.playerId],
            };
          }
          // M4 batch 6b: surface a "correct" verdict on the local guesser's
          // store so <VerdictDisplay> can render the inline confirmation.
          // GUESS_CORRECT is broadcast to everyone (audience sees who got
          // it), but only the guesser themselves needs the personal
          // verdict echo.
          if (frame.playerId === currentPlayerId) {
            next.lastVerdict = {
              kind: "correct",
              // Server doesn't echo the guesser's text on GUESS_CORRECT;
              // the verdict carries an empty text and the UI shows the
              // canonical word (post-redaction the client now sees the
              // un-redacted word on its own GUESS_CORRECT).
              text: "",
              at: Date.now(),
            };
          }
          return next;
        });
        break;

      // ── M4 batch 6b commit 3: guess verdict whispers (server routes
      // these to the guesser only via eventsToWire / §5.12). ──────────────
      case "GUESS_CLOSE":
        patchGame((prior) => {
          if (!prior) return prior;
          return {
            ...prior,
            lastVerdict: { kind: "close", text: frame.text, at: Date.now() },
          };
        });
        break;

      case "GUESS_MISS":
        patchGame((prior) => {
          if (!prior) return prior;
          return {
            ...prior,
            lastVerdict: { kind: "miss", text: frame.text, at: Date.now() },
          };
        });
        break;

      // V4.2.T10b: public wrong-guess feed. The server already redacted this
      // per recipient (drawer never receives `miss` text; `correct` lines
      // carry no word), so we append verbatim to the bounded feed.
      case "CHAT_MESSAGE":
        appendChatLine({
          key: `c${chatKeySeq++}`,
          playerId: frame.playerId,
          name: frame.name,
          color: frame.color,
          kind: frame.chatKind,
          ...(frame.chatKind === "miss" && frame.text !== undefined ? { text: frame.text } : {}),
        });
        break;

      case "DRAWER_DISCONNECT_GRACE":
        patchGame((prior) => {
          if (!prior) return prior;
          return {
            ...prior,
            drawerDisconnect: {
              drawerId: frame.playerId,
              graceUntil: frame.graceUntil,
            },
          };
        });
        break;

      case "DRAWER_RECONNECT":
        patchGame((prior) => {
          if (!prior) return prior;
          // Race protection: only clear if the reconnecting player matches
          // the currently-tracked disconnect. A stale event arriving after
          // the banner cleared is a no-op.
          if (prior.drawerDisconnect?.drawerId !== frame.playerId) return prior;
          const { drawerDisconnect: _dropped, ...rest } = prior;
          void _dropped;
          return rest;
        });
        break;

      case "PLAYER_LEAVE_NOTICE":
        patchGame((prior) => {
          if (!prior) return prior;
          return {
            ...prior,
            lastLeaveNotice: {
              displayName: frame.displayName,
              reason: frame.reason,
              at: Date.now(),
            },
          };
        });
        break;

      // ── M4 batch 7 commit 3: drawer canvas mutations broadcast ──────
      // Both STROKE_UNDO_BROADCAST and STROKE_CLEAR_BROADCAST go to every
      // recipient (canvas state is public). The drawer themselves also
      // receives these: their local optimistic pop / clear already
      // happened (commit 4 wires that), so the store mutation here is a
      // no-op for the drawer (popStrokeById returns false for an unknown
      // id; clearStrokes on an empty log is also a no-op).
      case "STROKE_UNDO_BROADCAST":
        popStrokeById(frame.strokeId);
        break;

      case "STROKE_CLEAR_BROADCAST":
        // v4 T3: only the session that performed the optimistic clear skips the
        // in-progress reset (it already flushed; re-flushing would drop a NEW
        // stroke begun during the round-trip). An external clear, the same
        // drawer's other tab/device: has no pending echo here and DOES flush.
        if (pendingClearEchoes > 0) {
          pendingClearEchoes--;
          clearStrokes(false); // our own echo: sync the log, keep any new stroke
        } else {
          clearStrokes(true); // external clear: flush this session's held stroke
        }
        // Either way, in-flight remote strokes were wiped by the clear.
        clearProvisionals();
        // Also drop any pending stroke metadata we'd been collecting from
        // STROKE_START broadcasts but never matched to a STROKE_END.
        // Otherwise a stuck-pending entry would leak forever.
        pendingStrokeMeta.clear();
        break;

      // v4 T1 (D5): a fill is now a real synced op. Append it to the same
      // ordered log as strokes so the canvas replays it in sequence. The
      // drawer also receives its own echo, dedup by op id makes that a
      // no-op against the optimistic local append.
      case "FILL_BROADCAST":
        appendFill({
          strokeId: frame.strokeId,
          point: frame.point,
          color: frame.color as FinalizedFill["color"],
        });
        break;

      case "AUTH_REJECT":
        // Handshake-fatal reject: the resume token isn't in the server's
        // session registry (server restart, or a future kick-eviction). The
        // server closes the socket right after sending this, and
        // ReconnectingWebSocket would retry the same dead token forever,
        // stop the loop and surface a terminal state so the shell can fall
        // back to a fresh join (audit 2026-06-11).
        if (frame.reason === "invalid-session-token") {
          rws.close(1000, "invalid-session-token");
          onState({ kind: "auth-rejected", reason: frame.reason });
          break;
        }
        // Action reject on an open socket (e.g. "not-authorized-start" after
        // a phase race): patch it onto the in-room state so the lobby can
        // reset its "Starting…" latch. A pre-room action reject has no state
        // to patch: log-only.
        if (lastInRoom) {
          patchInRoom({ lastAuthReject: { reason: frame.reason, at: Date.now() } });
        } else {
          console.warn(`ws: AUTH_REJECT (${frame.reason}) before room state — ignored`);
        }
        break;

      // The remaining M4 frames have no store mutation yet, UI for these
      // is a follow-up. The cases exist so the switch is exhaustive and
      // future unknown frames don't silently drop wire-version updates.
      case "ALL_GUESSED":
      case "REACTION":
        // Intentional no-op: reactions overlay land in a follow-up batch.
        break;
    }
  });

  rws.addEventListener("close", (ev: { code?: number; reason?: string }) => {
    // ReconnectingWebSocket re-opens after any close, right for transient
    // drops (1006 etc.), wrong for a policy close. Code 1008 is the server
    // saying "go away" (host kick, or connection rate-limit at accept):
    // retrying would either silently rejoin a room we were kicked from
    // (the session stays alive server-side today) or hammer the limiter.
    // Stop the retry loop and surface a terminal state (audit 2026-06-11).
    if (ev.code !== 1008) return;
    rws.close(1000, "policy-close");
    if (ev.reason === "kicked") {
      onState({ kind: "kicked" });
    } else {
      onState({ kind: "error", message: "Too many connections — give it a minute." });
    }
  });

  rws.addEventListener("error", () => {
    onState({ kind: "error", message: "connection failed" });
  });

  return {
    close: () => rws.close(1000, "client-shutdown"),
    send: (frame: ClientControlFrame): boolean => {
      // ReconnectingWebSocket exposes the current open socket via `readyState`.
      if (rws.readyState !== 1 /* OPEN */) return false;
      rws.send(JSON.stringify(frame));
      // v4 T3: remember our own STROKE_CLEAR so its echoed STROKE_CLEAR_BROADCAST
      // is treated as a no-reset echo (we already flushed optimistically).
      if (frame.kind === "STROKE_CLEAR") pendingClearEchoes++;
      return true;
    },
    sendBinary: (buf: ArrayBuffer): boolean => {
      if (rws.readyState !== 1 /* OPEN */) return false;
      rws.send(buf);
      return true;
    },
  };
}
